import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    RadioGroup,
    Radio,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Autocomplete,
    Typography,
    Box,
    Card,
    Popper,
    CardContent,
    Divider,
    Chip,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Grid,
    Stack,
    Avatar,
    Badge,
    Tooltip,
    Fade,
    Zoom,
    Slide,
    Collapse,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
} from '@mui/material';
import ReceiptCustomizationDialog from '../../../Components/Widgets/ReceiptCustomizationDialog/ReceiptCustomizationDialog.jsx';
import {
    styled,
    createTheme,
    ThemeProvider,
    alpha,
} from '@mui/material/styles';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
    ChevronLeft as ChevronLeftIcon,
    AccountCircle as AccountCircleIcon,
    Business as BusinessIcon,
    Receipt as ReceiptIcon,
    Payment as PaymentIcon,
    AttachMoney as AttachMoneyIcon,
    CreditCard as CreditCardIcon,
    AccountBalance as AccountBalanceIcon,
    LocalAtm as LocalAtmIcon,
    Calculate as CalculateIcon,
    RoundedCorner as RoundedCornerIcon,
    EmailOutlined as EmailOutlinedIcon,
    PhoneOutlined as PhoneOutlinedIcon,
    PlaceOutlined as PlaceOutlinedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { dir } from 'i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import he from 'date-fns/locale/he';
import arSA from 'date-fns/locale/ar-SA';
import enUS from 'date-fns/locale/en-US';
import ItemSelectionModal from '../../../Components/ItemSelectionModal';
import AutocompleteSelectFilter from '../../../Components/Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import DesignVersionField from '../../../Components/DesignVersionField/DesignVersionField.jsx';
import { shouldShowDesignVersionPicker } from '../../../Utils/DocumentVersion/documentDesignVersions.js';
import fieldStyles from '../../../Components/JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import styles from './EnhancedCreateReceipt.module.scss';
import ProjectsService from '../../../Services/projects.service.js';
import ProjectModal from '../../../Components/Projects/ProjectModal.jsx';
import LinkDocumentsModal from '../../../Components/TransactionDocuments/SubComponents/LinkDocumentsModal.jsx';
import PendingLinksSummary from '../../../Components/TransactionDocuments/PendingLinksSummary.jsx';
import { toast } from 'react-toastify';
import Constants from '../../../assests/Constants/constants.js';

// Enhanced Theme
const enhancedTheme = createTheme({
    palette: {
        primary: {
            main: '#304FFF',
            light: '#5C7CFF',
            dark: '#1E40FF',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#F9AA2A',
            light: '#FFB74D',
            dark: '#F57C00',
            contrastText: '#000000',
        },
        background: {
            default: '#F8FAFC',
            paper: '#FFFFFF',
        },
        text: {
            primary: '#1A202C',
            secondary: '#4A5568',
        },
        error: {
            main: '#E53E3E',
            light: '#FC8181',
        },
        success: {
            main: '#38A169',
            light: '#68D391',
        },
        warning: {
            main: '#ffca72',
            light: '#F6E05E',
        },
        info: {
            main: '#3182CE',
            light: '#63B3ED',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            lineHeight: 1.2,
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 600,
            lineHeight: 1.3,
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h5: {
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.6,
        },
        button: {
            textTransform: 'none',
            fontWeight: 600,
        },
    },
    shape: {
        borderRadius: 12,
    },
    shadows: [
        'none',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.1)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 16px 32px rgba(0, 0, 0, 0.1)',
        '0px 32px 64px rgba(0, 0, 0, 0.1)',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.1)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 16px 32px rgba(0, 0, 0, 0.1)',
        '0px 32px 64px rgba(0, 0, 0, 0.1)',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.1)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 16px 32px rgba(0, 0, 0, 0.1)',
        '0px 32px 64px rgba(0, 0, 0, 0.1)',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.1)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 16px 32px rgba(0, 0, 0, 0.1)',
        '0px 32px 64px rgba(0, 0, 0, 0.1)',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.1)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 16px 32px rgba(0, 0, 0, 0.1)',
        '0px 32px 64px rgba(0, 0, 0, 0.1)',
    ],
});

// Enhanced Styled Components
const StyledFormContainer = styled(Box)(({ theme, direction }) => ({
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    textAlign: direction === 'rtl' ? 'right' : 'left',
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(2),
    },
}));

const StyledFormCard = styled(Card)(({ theme, direction }) => ({
    background: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    overflow: 'hidden',
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    marginBottom: theme.spacing(0.35),
}));

const StyledFormSection = styled(Box)(({ theme, direction }) => ({
    padding: theme.spacing(1),
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    '&:last-child': {
        borderBottom: 'none',
    },
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(2),
    },
}));

const StyledSectionHeader = styled(Box)(({ theme, direction }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(0.3),
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
}));
const StyledSectionTitle = styled(Typography)(({ theme, direction }) => ({
    color: theme.palette.text.primary,
    paddingBottom: theme.spacing(0.3),
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
}));





/* Use fieldStyles for input chrome — avoid Emotion ::placeholder (breaks stylis/RTL). */
const getFieldClassName = (hasError = false, extra = '') =>
    [fieldStyles.styleInput, styles.softField, hasError ? fieldStyles.styleInputRequired : '', extra]
        .filter(Boolean)
        .join(' ');

/** Table multiline chrome — native textarea resize (avoids styleInput height lock + TextareaAutosize). */
const getTableMultilineClassName = (hasError = false, extra = '') =>
    [styles.tableCellField, hasError ? styles.tableCellFieldError : '', extra]
        .filter(Boolean)
        .join(' ');

const tableMultilineInputProps = (base = {}) => ({
    ...base,
    disableUnderline: true,
    inputComponent: 'textarea',
});

const ComboPopper = React.forwardRef(({ direction: popperDirection = 'rtl', ...props }, ref) => (
    <Popper
        {...props}
        ref={ref}
        dir={popperDirection}
        className={`${props.className || ''} ${fieldStyles.comboPopper}`}
        placement={popperDirection === 'rtl' ? 'bottom-end' : 'bottom-start'}
        style={{ ...props.style, zIndex: 1600 }}
        modifiers={[
            ...(props.modifiers || []),
            {
                name: 'offset',
                options: {
                    // Match AutocompleteSelectFilter portal gap (Document Language)
                    offset: [0, 8],
                },
            },
        ]}
    />
));
ComboPopper.displayName = 'ComboPopper';

const StyledButton = styled(Button)(({ theme, variant = 'contained', direction, size = 'medium' }) => ({
    borderRadius: theme.shape.borderRadius,
    padding: size === 'small' ? theme.spacing(0.75, 1.5) : 
             size === 'medium' ? theme.spacing(1, 2) : 
             theme.spacing(1.5, 3),
    fontWeight: 600,
    textTransform: 'none',
    fontSize: size === 'small' ? '0.875rem' : 
              size === 'medium' ? '0.9rem' : 
              '1rem',
    minHeight: size === 'small' ? '32px' : 
               size === 'medium' ? '40px' : 
               '48px',
    transition: 'all 0.3s ease-in-out',
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: theme.shadows[4],
    },
    ...(variant === 'contained' && {
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        },
    }),
    '&.Mui-disabled': {
        background: '#ccc !important',
        color: 'white !important',
        '&:hover': {
            background: '#ccc !important',
            transform: 'none',
            boxShadow: 'none',
        },
    },
}));

const StyledTableContainer = styled(TableContainer)(({ theme, direction }) => ({
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    overflowX: 'auto',
    overflowY: 'visible',
    boxShadow: theme.shadows[1],
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
}));

const StyledTable = styled(Table)(({ theme, direction }) => ({
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    '& .MuiTableCell-head': {
        backgroundColor: '#eef4fe',
        color: '#1d2452',
        fontWeight: 600,
        fontSize: '0.8125rem',
        lineHeight: 1.3,
        letterSpacing: 0,
        padding: theme.spacing(1.5, 1.5),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        textAlign: direction === 'rtl' ? 'right' : 'left',
    },
    '& .MuiTableCell-body': {
        backgroundColor: '#fff',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        padding: theme.spacing(0.75, 0.75),
        textAlign: direction === 'rtl' ? 'right' : 'left',
    },
    '& .MuiTableRow-root:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
    },
}));

// Enhanced CreateReceipt Component
const EnhancedCreateReceipt = ({
    designVersions = [],
    formData,
    setFormData,
    handleChange,
    handleAccountCodeChange,
    handleCurrencyCodeChange,
    handlePriceIncludingVATChange,
    handleItemVATChange,
    handleInvoiceItem,
    handleMakatBlurOrEnter,
    handleItemChange,
    handleItemDiscountAmountChange,
    handleChangeItemPrice,
    handleRemoveItem,
    handleAddItem,
    handleAddReceiptItem,
    handleCreateDraftInvoice,
    handleCloseAddNewAccountCardDocument,
    handleCloseAddItem,
    onDateChange,
    datePickerKey,
    computeRowTotals,
    beautifyNumber,
    styledInput,
    Constants,
    direction,
    t,
    currentUILanguage,
    showNoTaxCheckbox,
    addNewAccountCardDocument,
    showAllAccountCards = false,
    setShowAllAccountCards,
    formattedAccountCardOptions,
    fieldErrors,
    allClientItemFieldsFilled,
    invoiceModalType,
    client,
    clientItems,
    addItemFlags,
    isAmountAboveLimit,
    totalInvoiceAmount,
    totalReceiptAmount,
    totalVATAmount,
    totalAmountWithoutVAT,
    totalsVisible,
    setTotalsVisible,
    withholdingTaxRowAdded,
    setWithholdingTaxRowAdded,
    handleAddWithholdingTaxClick,
    handleRemoveWithholdingTaxRow,
    handleRoundReceipt,
    receiptClientDeductionRatePercent = null,
    receiptWithholdingFormulaAmount = 0,
    calculatedWithholdingAmount = 0,
    autoClientWithholding = false,
    handleReceiptAutoWithholdingChange,
    withholdingPercent = '',
    handleWithholdingPercentChange,
    renderReceiptRows,
    renderReceiptBankDetailsBar,
    type,
    token,
    linking = null,
    AutocompleteSelect: _AutocompleteSelect,
    deferredReceipt,
    setDeferredReceipt,
    allItemsHaveNoVATCalculation,
    itemsDisplayMode = 'normal',
    segmentationProjects = [],
    selectedSegmentation = null,
    onSegmentationChange,
    refreshSegmentationProjects,
}) => {
    const [activeStep, setActiveStep] = useState(0);
    const [expandedSection, setExpandedSection] = useState('client-info');
    const [isTotalsHovered, setIsTotalsHovered] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [itemSelectionModal, setItemSelectionModal] = useState({ open: false, rowIndex: null });
    const [selectedItem, setSelectedItem] = useState(null);
    const [projectModalOpen, setProjectModalOpen] = useState(false);
    const [projectCreateLoading, setProjectCreateLoading] = useState(false);
    const fieldTextAlign = direction === 'ltr' ? 'left' : 'right';
    /** Icons on the visual left (same side as DatePicker calendar). */
    const sideIconPosition = direction === 'rtl' ? 'end' : 'start';
    const comboInputProps = { disableUnderline: true };
    const { i18n } = useTranslation();
    const renderComboPopper = useCallback(
        (props) => <ComboPopper {...props} direction={direction} />,
        [direction]
    );

    const segmentationAutocompleteOptions = useMemo(
        () => [
            { value: '__ADD_NEW__', label: t('AddProject'), kind: 'add' },
            ...segmentationProjects.map((p) => ({
                value: String(p.id),
                label: `${p.code} — ${p.name}`,
                kind: 'project',
            })),
        ],
        [segmentationProjects, t]
    );

    const segmentationAutocompleteValue = useMemo(() => {
        if (!selectedSegmentation?.id) return null;
        return (
            segmentationAutocompleteOptions.find(
                (o) => o.kind === 'project' && String(o.value) === String(selectedSegmentation.id)
            ) ?? null
        );
    }, [selectedSegmentation, segmentationAutocompleteOptions]);

    const handleSegmentationAutocompleteChange = useCallback(
        (_event, newValue) => {
            if (newValue?.kind === 'add' || newValue?.value === '__ADD_NEW__') {
                setProjectModalOpen(true);
                return;
            }
            if (!newValue || newValue.value === '') {
                if (onSegmentationChange) onSegmentationChange(null);
                return;
            }
            if (onSegmentationChange) {
                onSegmentationChange({
                    id: String(newValue.value),
                    label: newValue.label,
                });
            }
        },
        [onSegmentationChange]
    );

    const handleProjectModalClose = useCallback(() => {
        setProjectModalOpen(false);
    }, []);

    const handleProjectModalSave = useCallback(
        async (projectInput) => {
            if (!client?.caseId || !token) return false;
            const drafts = Array.isArray(projectInput) ? projectInput : [projectInput];
            const normalized = drafts.map((d) => ({
                code: String(d?.code ?? '').trim(),
                name: String(d?.name ?? '').trim(),
            }));
            if (normalized.some((d) => !d.code || !d.name)) return false;

            setProjectCreateLoading(true);
            try {
                let lastCreated = null;
                for (const draft of normalized) {
                    const res = await ProjectsService.createProject(
                        { CaseId: client.caseId, Code: draft.code, Name: draft.name },
                        token
                    );
                    if (!res.ok) {
                        const msg = await res.text();
                        toast.error(msg || t('ErrorCreatingProject'));
                        if (refreshSegmentationProjects) await refreshSegmentationProjects();
                        return false;
                    }
                    lastCreated = await res.json();
                }
                toast.success(`${t('Add')} ${t('Projects')}`);
                if (refreshSegmentationProjects) await refreshSegmentationProjects();
                setProjectModalOpen(false);
                if (lastCreated?.id && onSegmentationChange) {
                    onSegmentationChange({
                        id: String(lastCreated.id),
                        label: `${lastCreated.code} — ${lastCreated.name}`,
                    });
                }
                return true;
            } catch (e) {
                console.error(e);
                toast.error(t('ErrorCreatingProject'));
                return false;
            } finally {
                setProjectCreateLoading(false);
            }
        },
        [client?.caseId, token, t, refreshSegmentationProjects, onSegmentationChange]
    );
    const dateAdapterLocale = useMemo(() => {
        switch (i18n.language) {
            case 'ar':
                return arSA;
            case 'he':
                return he;
            default:
                return enUS;
        }
    }, [i18n.language]);
    const toPickerDate = useCallback((value) => {
        if (value == null || value === '') return null;
        const d = value instanceof Date ? value : new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }, []);
    
    // Customization dialog state
    const [customizationDialogOpen, setCustomizationDialogOpen] = useState(false);
    const [customizationData, setCustomizationData] = useState({
        signature: client?.signature || '',
        profilePicture: client?.profilePicture || '',
        description: formData.invoiceDescription || '',
        footer: formData.invoiceFooter || '',
        columns: formData.columnSettings || {
            showOnInvoice: true,
            date: { visible: true, width: 120 },
            productService: { visible: true, width: 200, includeDescription: true, includeCategory: false },
            description: { visible: true, width: 250, includeQuantityAndRate: true },
            quantity: { visible: false, width: 100 },
            rate: { visible: false, width: 100 },
            amount: { visible: true, width: 120 },
            sku: { visible: false, width: 100 }
        }
    });

    // Debounce timer ref for date changes
    const dateChangeTimerRef = useRef(null);

    const steps = [
        { label: t('ClientInformation'), icon: <AccountCircleIcon /> },
        { label: t('InvoiceDetails'), icon: <ReceiptIcon /> },
        { label: t('ReceiptItems'), icon: <PaymentIcon /> },
    ];

    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    const handleSectionToggle = (section) => {
        setExpandedSection(expandedSection === section ? null : section);
    };

    // Debounced date change handler
    const debouncedDateChange = useCallback((date) => {
        // Clear existing timer
        if (dateChangeTimerRef.current) {
            clearTimeout(dateChangeTimerRef.current);
        }

        // Set new timer
        dateChangeTimerRef.current = setTimeout(() => {
            if (date) {
                onDateChange(date);
            }
        }, 800); // 800ms debounce delay
    }, [onDateChange]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (dateChangeTimerRef.current) {
                clearTimeout(dateChangeTimerRef.current);
            }
        };
    }, []);

    // Customization dialog handlers
    const handleOpenCustomizationDialog = () => {
        setCustomizationDialogOpen(true);
    };

    // Check if charity organization
    const isCharityOrganization = client?.caseType === Constants.CharityOrganization;
    console.log('isCharityOrganization', isCharityOrganization);
    // Get the currently selected payment method type (if any)
    const getCurrentPaymentMethodType = () => {
        if (formData.receiptItems?.checks && formData.receiptItems.checks.length > 0) return 'checks';
        if (formData.receiptItems?.creditCards && formData.receiptItems.creditCards.length > 0) return 'creditCards';
        if (formData.receiptItems?.cash && formData.receiptItems.cash.length > 0) return 'cash';
        if (formData.receiptItems?.bankTransactions && formData.receiptItems.bankTransactions.length > 0) return 'bankTransactions';
        if (formData.receiptItems?.offsets && formData.receiptItems.offsets.length > 0) return 'offsets';
        return null;
    };

    // Handle payment method selection
    const handlePaymentMethodSelect = (paymentType) => {
        const currentType = getCurrentPaymentMethodType();
        
        // For charity organizations, if switching to a different payment method type, clear other types
        // But allow multiple items of the same type
        if (isCharityOrganization && currentType && currentType !== paymentType) {
            // Clear all payment methods of other types, keep the new type empty (will be added)
            setFormData(prev => ({
                ...prev,
                receiptItems: {
                    ...prev.receiptItems,
                    checks: paymentType === 'checks' ? (prev.receiptItems?.checks || []) : [],
                    creditCards: paymentType === 'creditCards' ? (prev.receiptItems?.creditCards || []) : [],
                    cash: paymentType === 'cash' ? (prev.receiptItems?.cash || []) : [],
                    bankTransactions: paymentType === 'bankTransactions' ? (prev.receiptItems?.bankTransactions || []) : [],
                    offsets: paymentType === 'offsets' ? (prev.receiptItems?.offsets || []) : []
                }
            }));
        }
        
        setSelectedPaymentMethod(paymentType);
        handleAddReceiptItem(paymentType);
    };

    // Get payment method display name
    const getPaymentMethodDisplayName = (paymentType) => {
        const paymentMethodNames = {
            'checks': t('AddCheck'),
            'creditCards': t('AddCreditCard'),
            'cash': t('AddCash'),
            'bankTransactions': t('AddBankTransfer'),
            'offsets': t('AddOffset')
        };
        return paymentMethodNames[paymentType] || paymentType;
    };

    const handleCloseCustomizationDialog = () => {
        setCustomizationDialogOpen(false);
    };

    const handleCustomizationSave = async (data) => {
        try {
            // Update formData with customization data including signature and profile picture
            const updatedFormData = {
                ...formData,
                invoiceSignature: data.signature || formData.invoiceSignature,
                invoiceProfilePicture: data.profilePicture || formData.invoiceProfilePicture,
                invoiceDescription: data.description || formData.invoiceDescription,
                invoiceFooter: data.footer || formData.invoiceFooter,
                columnSettings: data.columnSettings || formData.columnSettings
            };
            
            // Update the main formData state
            setFormData(updatedFormData);
            
            // Call the original handleCreateDraftInvoice with updated data
            await handleCreateDraftInvoice(updatedFormData);
            
            // Close the customization dialog
            setCustomizationDialogOpen(false);
        } catch (error) {
            console.error('Error in customization save:', error);
        }
    };

    // Handle opening item selection modal
    const handleOpenItemSelectionModal = (rowIndex) => {
        const currentItem = formData.invoiceItems[rowIndex];
        const matchingItem = clientItems.find(item => item.name === currentItem.name);
        
        setSelectedItem(matchingItem || null);
        setItemSelectionModal({ open: true, rowIndex });
    };

    // Handle closing item selection modal
    const handleCloseItemSelectionModal = () => {
        setItemSelectionModal({ open: false, rowIndex: null });
    };

    // Handle confirming item selection
    const handleConfirmItemSelection = (item, customName) => {
        if (itemSelectionModal.rowIndex !== null && item) {
            const newValue = {
                value: item.makat,
                label: customName || item.name,
            };
            
            handleInvoiceItem(itemSelectionModal.rowIndex, newValue);
            
            if (customName && customName !== item.name) {
                setTimeout(() => {
                    handleItemChange(itemSelectionModal.rowIndex, 'name', customName);
                }, 0);
            }
        }
    };

    const linkingSummaryExtra = linking ? {
        caseId: client?.caseId,
        token,
        draftItems: linking.draft?.items || [],
        onLinkItemsLoaded: linking.setLinkItems,
    } : {};

    return (
        <ThemeProvider theme={enhancedTheme}>
            <StyledFormContainer className={styles.root} dir={direction === 'rtl' ? 'rtl' : 'ltr'}>
                
                {/* Receipt Type Selection */}
                {invoiceModalType === 6 && (
                    <StyledFormCard dir={direction}>
                        <StyledFormSection dir={direction}>
                            <StyledSectionHeader dir={direction}>
                                <StyledSectionTitle variant="h5" dir={direction}>
                                    <ReceiptIcon color="primary" />
                                    {t('SelectReceiptType')}
                                </StyledSectionTitle>
                                <Chip
                                    label={deferredReceipt ? t('DeferredReceipt') : t('Receipt')}
                                    color="primary"
                                    size="small"
                                />
                            </StyledSectionHeader>

                            <div className={styles.clientInfoGrid}>
                                <div className={styles.formRowItem}>
                                    <div className={styles.clientNameField}>
                                        <AutocompleteSelectFilter
                                            options={[
                                                { value: 'Receipt', label: t('Receipt') },
                                                { value: 'DeferredReceipt', label: t('DeferredReceipt') },
                                            ]}
                                            selectedValues={
                                                deferredReceipt
                                                    ? { value: 'DeferredReceipt', label: t('DeferredReceipt') }
                                                    : { value: 'Receipt', label: t('Receipt') }
                                            }
                                            onChange={(event, newValue) => {
                                                if (newValue) {
                                                    setDeferredReceipt(newValue.value === 'DeferredReceipt');
                                                }
                                            }}
                                            placeholder={t('SelectReceiptType')}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                        />
                                    </div>
                                </div>
                                {linking && (
                                    <div className={styles.formRowItem}>
                                        <PendingLinksSummary
                                            direction={direction}
                                            pendingLinks={linking.pendingLinks}
                                            total={linking.total}
                                            validation={linking.validation}
                                            canOpen={linking.canOpen}
                                            onOpen={linking.openModal}
                                            onRemove={linking.removeLink}
                                            {...linkingSummaryExtra}
                                        />
                                    </div>
                                )}
                            </div>
                        </StyledFormSection>
                    </StyledFormCard>
                )}

                {linking && (
                    <LinkDocumentsModal
                        open={linking.modalOpen}
                        onClose={linking.closeModal}
                        caseId={client?.caseId}
                        token={token}
                        direction={direction}
                        mode="create"
                        draft={linking.draft}
                        pendingLinks={linking.pendingLinks}
                        onConfirm={linking.confirm}
                    />
                )}
                {/* Tax Options */}
                {showNoTaxCheckbox && (
                    <StyledFormCard dir={direction}>
                        <StyledFormSection dir={direction}>
                            <StyledSectionHeader dir={direction}>
                                <StyledSectionTitle variant="h5" dir={direction}>
                                    <InfoIcon color="primary" />
                                    {t('TaxOptions')}
                                </StyledSectionTitle>
                                <Chip
                                    label={
                                        formData.forceNoTax 
                                            ? t('InvoiceWithNoTax')
                                            : formData.noNeedforConfirmationNumber 
                                                ? t('NoNeedForConfirmationNumber')
                                                : t('Draft')
                                    }
                                    color={
                                        formData.forceNoTax || formData.noNeedforConfirmationNumber
                                            ? "primary"
                                            : "warning"
                                    }
                                    size="small"
                                />
                            </StyledSectionHeader>

                            <div className={styles.modeToggles} dir={direction} style={{ alignItems: 'center' }}>
                                <div
                                    className={`${styles.modeToggle}${formData.forceNoTax ? ` ${styles.isChecked}` : ''}`}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.forceNoTax}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        forceNoTax: e.target.checked,
                                                        // Uncheck the other option when this is checked
                                                        noNeedforConfirmationNumber: e.target.checked ? false : prev.noNeedforConfirmationNumber,
                                                    }))
                                                }
                                                size="small"
                                            />
                                        }
                                        label={t('InvoiceWithNoTax')}
                                    />
                                </div>
                                <div
                                    className={`${styles.modeToggle}${formData.noNeedforConfirmationNumber ? ` ${styles.isChecked}` : ''}`}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.noNeedforConfirmationNumber}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        noNeedforConfirmationNumber: e.target.checked,
                                                        // Uncheck the other option when this is checked
                                                        forceNoTax: e.target.checked ? false : prev.forceNoTax,
                                                    }))
                                                }
                                                size="small"
                                            />
                                        }
                                        label={t('NoNeedForConfirmationNumber')}
                                    />
                                </div>
                                {linking && (
                                    <PendingLinksSummary
                                        direction={direction}
                                        fullWidth={false}
                                        pendingLinks={linking.pendingLinks}
                                        total={linking.total}
                                        validation={linking.validation}
                                        canOpen={linking.canOpen}
                                        onOpen={linking.openModal}
                                        onRemove={linking.removeLink}
                                        {...linkingSummaryExtra}
                                    />
                                )}
                            </div>
                        </StyledFormSection>
                    </StyledFormCard>
                )}
                {/* Charity Organization Confirmation Number Option */}
                {invoiceModalType === 6 && isCharityOrganization && (
                    <StyledFormCard dir={direction}>
                        <StyledFormSection dir={direction}>
                            <StyledSectionHeader dir={direction}>
                                <StyledSectionTitle variant="h5" dir={direction}>
                                    <InfoIcon color="primary" />
                                    {t('CharityOrganizationConfirmationNumberOption')}
                                </StyledSectionTitle>
                                <Chip
                                    label={
                                        formData.noNeedforConfirmationNumber 
                                            ? t('NoNeedForConfirmationNumber')
                                            : t('Draft')
                                    }
                                    color={
                                        formData.noNeedforConfirmationNumber
                                            ? "primary"
                                            : "warning"
                                    }
                                    size="small"
                                />
                            </StyledSectionHeader>

                            <div className={styles.modeToggles} dir={direction}>
                                <div
                                    className={`${styles.modeToggle}${formData.noNeedforConfirmationNumber ? ` ${styles.isChecked}` : ''}`}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.noNeedforConfirmationNumber}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        noNeedforConfirmationNumber: e.target.checked,
                                                    }))
                                                }
                                                size="small"
                                            />
                                        }
                                        label={t('NoNeedForConfirmationNumber')}
                                    />
                                </div>
                            </div>
                        </StyledFormSection>
                    </StyledFormCard>
                )}


                {/* Main Form */}
                <StyledFormCard dir={direction}>
                    <StyledFormSection dir={direction}>
                        <StyledSectionHeader dir={direction}>
                            <StyledSectionTitle variant="h5" dir={direction}>
                                <AccountCircleIcon color="primary" />
                                {t('ClientInformation')}
                            </StyledSectionTitle>
                            <Chip
                                label={t('Draft')}
                                color="warning"
                                size="small"
                            />
                        </StyledSectionHeader>
                    {/* Client Selection — row1: name, caseId, phone, currency, email, documentDate */}
                    <div className={styles.clientInfoGrid}>
                        <div className={styles.formRowItem}>
                            <div className={styles.fieldTitle}>
                                {t('ClientName')}<span className={styles.requiredAsterisk}>*</span>
                            </div>
                            {addNewAccountCardDocument ? (
                                <div className={styles.clientAddColumn}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleCloseAddNewAccountCardDocument}
                                        startIcon={<BusinessIcon />}
                                        className={styles.addCustomerBtn}
                                    >
                                        {t('ReturnToNamesList')}
                                    </Button>
                                    <TextField
                                        fullWidth
                                        placeholder={t('ClientName')}
                                        value={formData.invoiceClientName || ''}
                                        onChange={(e) => handleChange('invoiceClientName', e.target.value)}
                                        variant="standard"
                                        className={getFieldClassName(!formData.invoiceClientName)}
                                        InputProps={comboInputProps}
                                        inputProps={{
                                            style: {
                                                textAlign: fieldTextAlign,
                                            },
                                        }}
                                        required
                                    />
                                </div>
                            ) : (
                                <div
                                    className={`${styles.clientNameField}${!formData.invoiceAccountCodeId ? ` ${styles.clientFieldError}` : ''}`}
                                >
                                    <AutocompleteSelectFilter
                                        key={formData.invoiceAccountCodeId ?? 'empty'}
                                        options={formattedAccountCardOptions}
                                        selectedValues={
                                            formData.invoiceAccountCodeId
                                                ? formattedAccountCardOptions.find(
                                                    (opt) => opt.value === formData.invoiceAccountCodeId
                                                )
                                                : null
                                        }
                                        onChange={(e, newValue) => handleAccountCodeChange(newValue)}
                                        placeholder={t('Client Name')}
                                        isMultiple={false}
                                        direction={direction}
                                        searchable
                                        portalMenu
                                        emptyAction={{
                                            label: `+ ${t('addCustomer')}`,
                                            onClick: () => handleAccountCodeChange({ value1: 'AddItem' }),
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className={styles.formRowItem}>
                            <div className={styles.fieldTitle}>
                                {t('EnterClientCaseId')}
                                {(isAmountAboveLimit && !formData.noNeedforConfirmationNumber) && (
                                    <span className={styles.requiredAsterisk}>*</span>
                                )}
                            </div>
                            <TextField
                                fullWidth
                                placeholder={t('EnterClientCaseId')}
                                value={formData.invoiceClientCaseId}
                                onChange={(e) => handleChange('invoiceClientCaseId', e.target.value)}
                                variant="standard"
                                size="small"
                                className={getFieldClassName(isAmountAboveLimit && !formData.noNeedforConfirmationNumber && !formData.invoiceClientCaseId)}
                                InputProps={comboInputProps}
                                inputProps={{
                                    style: {
                                        textAlign: fieldTextAlign,
                                    },
                                }}
                                required={isAmountAboveLimit && !formData.noNeedforConfirmationNumber}
                            />
                        </div>

                        <div className={styles.formRowItem}>
                            <div className={styles.fieldTitle}>
                                {t('PhoneNumber')}<span className={styles.requiredAsterisk}>*</span>
                            </div>
                            <TextField
                                fullWidth
                                placeholder={t('PhoneNumber')}
                                value={formData.invoiceClientPhoneNumber || ''}
                                onChange={(e) => handleChange('invoiceClientPhoneNumber', e.target.value)}
                                variant="standard"
                                type="tel"
                                size="small"
                                required
                                className={getFieldClassName(false)}
                                InputProps={{
                                    ...comboInputProps,
                                    ...(sideIconPosition === 'start'
                                        ? {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneOutlinedIcon className={styles.fieldIcon} />
                                                </InputAdornment>
                                            ),
                                        }
                                        : {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <PhoneOutlinedIcon className={styles.fieldIcon} />
                                                </InputAdornment>
                                            ),
                                        }),
                                }}
                                inputProps={{
                                    style: {
                                        textAlign: fieldTextAlign,
                                    },
                                }}
                            />
                        </div>

                        <div className={styles.formRowItem}>
                            <div className={styles.fieldTitle}>
                                {t('Currency')}
                            </div>
                            <AutocompleteSelectFilter
                                options={Object.entries(Constants.ForeignCurrencyCode).map(
                                    ([code, num]) => ({
                                        value: num,
                                        label: `${code} ${Constants.ForeignCurrencySymbol[code]}`,
                                    })
                                )}
                                selectedValues={
                                    formData.foreignCurrencyCode != null
                                        ? (() => {
                                            const codeKey = Object.keys(Constants.ForeignCurrencyCode)
                                                .find((k) => Constants.ForeignCurrencyCode[k] === formData.foreignCurrencyCode);
                                            return {
                                                value: formData.foreignCurrencyCode,
                                                label: `${codeKey} ${Constants.ForeignCurrencySymbol[codeKey]}`,
                                            };
                                        })()
                                        : null
                                }
                                onChange={(e, newVal) => handleCurrencyCodeChange(newVal ? newVal.value : null)}
                                placeholder={t('Currency')}
                                isMultiple={false}
                                clearable
                                direction={direction}
                                portalMenu
                            />
                        </div>

                        <div className={styles.formRowItem}>
                            <div className={styles.fieldTitle}>
                                {t('EnterClientEmail')}
                            </div>
                            <TextField
                                fullWidth
                                placeholder={t('EnterClientEmail')}
                                value={formData.invoiceClientEmail || ''}
                                onChange={(e) => handleChange('invoiceClientEmail', e.target.value)}
                                variant="standard"
                                type="email"
                                size="small"
                                className={getFieldClassName(false)}
                                InputProps={{
                                    ...comboInputProps,
                                    ...(sideIconPosition === 'start'
                                        ? {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailOutlinedIcon className={styles.fieldIcon} />
                                                </InputAdornment>
                                            ),
                                        }
                                        : {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <EmailOutlinedIcon className={styles.fieldIcon} />
                                                </InputAdornment>
                                            ),
                                        }),
                                }}
                                inputProps={{
                                    style: {
                                        textAlign: fieldTextAlign,
                                    },
                                }}
                            />
                        </div>

                        <div className={styles.formRowItem}>
                            <div className={styles.fieldTitle}>
                                {t('Document Date')}<span className={styles.requiredAsterisk}>*</span>
                            </div>
                            <div className={styles.dateToggleGroup}>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateAdapterLocale}>
                                    <DatePicker
                                        key={`date-picker-${datePickerKey}`}
                                        value={toPickerDate(formData.invoiceDate)}
                                        onChange={(d) => {
                                            if (d) {
                                                debouncedDateChange(d);
                                            }
                                        }}
                                        format="dd/MM/yyyy"
                                        slotProps={{
                                            textField: {
                                                variant: 'standard',
                                                fullWidth: true,
                                                className: getFieldClassName(!formData.invoiceDate),
                                                InputProps: comboInputProps,
                                                required: true,
                                                error: !formData.invoiceDate,
                                                inputProps: {
                                                    style: { textAlign: fieldTextAlign },
                                                },
                                            },
                                            popper: {
                                                sx: { zIndex: 1700 },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </div>
                        </div>
                    </div>

                    {!addNewAccountCardDocument && (
                        <div className={styles.clientActionsRow}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleAccountCodeChange({ value1: 'AddItem' })}
                                className={styles.addCustomerBtn}
                            >
                                {t('addCustomer')}
                            </Button>
                            {typeof setShowAllAccountCards === 'function' && (
                                <div
                                    className={`${styles.modeToggle}${showAllAccountCards ? ` ${styles.isChecked}` : ''}`}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!showAllAccountCards}
                                                onChange={(e) => setShowAllAccountCards(e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label={t('ShowAllAccountCards')}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    <div className={styles.clientInfoSecondaryGrid}>
                        <div className={styles.formRowItem}>
                            <div className={styles.fieldTitle}>
                                {t('EnterClientLocation')}
                            </div>
                            <TextField
                                fullWidth
                                placeholder={t('EnterClientLocation')}
                                value={formData.invoiceClientLocation || ''}
                                onChange={(e) => handleChange('invoiceClientLocation', e.target.value)}
                                variant="standard"
                                size="small"
                                className={getFieldClassName(false)}
                                InputProps={{
                                    ...comboInputProps,
                                    ...(sideIconPosition === 'start'
                                        ? {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PlaceOutlinedIcon className={styles.fieldIcon} />
                                                </InputAdornment>
                                            ),
                                        }
                                        : {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <PlaceOutlinedIcon className={styles.fieldIcon} />
                                                </InputAdornment>
                                            ),
                                        }),
                                }}
                                inputProps={{
                                    style: {
                                        textAlign: fieldTextAlign,
                                    },
                                }}
                            />
                        </div>

                        <div className={styles.formRowItem}>
                            <div className={styles.fieldTitle}>
                                {t('enterDescription')}
                            </div>
                            <TextField
                                fullWidth
                                placeholder={t('enterDescription')}
                                value={formData.invoiceDescription || ''}
                                onChange={(e) => handleChange('invoiceDescription', e.target.value)}
                                variant="standard"
                                multiline
                                minRows={2}
                                maxRows={8}
                                size="small"
                                className={styles.multilineField}
                                InputProps={comboInputProps}
                                inputProps={{
                                    style: {
                                        textAlign: fieldTextAlign,
                                    },
                                }}
                                helperText={t('descriptionNewLineHint')}
                            />
                        </div>
                    </div>
                </StyledFormSection>
                </StyledFormCard>


                {/* Invoice Details Section */}
                {(invoiceModalType !== 6) && (
                    !(client && client.caseType && Constants.TaxExemptTypes.includes(client.caseType))) && (
                    <StyledFormCard  dir={direction}>
                        <StyledFormSection dir={direction}>
                            {!formData.forceNoTax && (
                                <>
                            <StyledSectionHeader dir={direction}>
                                <StyledSectionTitle variant="h5" dir={direction}>
                                    <ReceiptIcon color="primary" />
                                    {t('InvoiceDetails')}
                                </StyledSectionTitle>
                                <Chip
                                    label={
                                        formData.forceNoTax 
                                            ? t('InvoiceWithNoTax')
                                            : formData.priceIncludingVAT 
                                                ? t('PriceIncludingVAT') 
                                                : t('PriceExcludingVAT')
                                    }
                                    color={formData.forceNoTax ? "error" : "info"}
                                    size="small"
                                />
                            </StyledSectionHeader>

                            <div className={styles.modeToggles} dir={direction}>
                                <RadioGroup
                                    row
                                    value={formData.priceIncludingVAT.toString()}
                                    onChange={handlePriceIncludingVATChange}
                                    sx={{
                                        flexDirection: 'row',
                                        flexWrap: 'wrap',
                                        gap: '6px',
                                        dir: direction,
                                    }}
                                >
                                    <div
                                        className={`${styles.modeToggle}${formData.priceIncludingVAT ? ` ${styles.isChecked}` : ''}`}
                                    >
                                        <FormControlLabel
                                            value="true"
                                            control={<Radio size="small" />}
                                            label={t('PriceIncludingVAT')}
                                        />
                                    </div>
                                    <div
                                        className={`${styles.modeToggle}${!formData.priceIncludingVAT ? ` ${styles.isChecked}` : ''}`}
                                    >
                                        <FormControlLabel
                                            value="false"
                                            control={<Radio size="small" />}
                                            label={t('PriceExcludingVAT')}
                                        />
                                    </div>
                                </RadioGroup>
                            </div>
                                
                                {/* Document Language (+ Projects for receipt type 5) */}
                                <div className={styles.languageProjectsRow} dir={direction}>
                                <div className={styles.languageField}>
                                    <div className={styles.fieldTitle}>
                                        {t('Document Language')}
                                    </div>
                                    <AutocompleteSelectFilter
                                        options={[
                                            { value: 'he', label: 'עברית' },
                                            { value: 'ar', label: 'العربية' },
                                            { value: 'en', label: 'English' }
                                        ]}
                                        selectedValues={(() => {
                                            const lang = formData.language || currentUILanguage || 'he';
                                            const options = [
                                                { value: 'he', label: 'עברית' },
                                                { value: 'ar', label: 'العربية' },
                                                { value: 'en', label: 'English' }
                                            ];
                                            return options.find((o) => o.value === lang) || options[0];
                                        })()}
                                        onChange={(_, selectedOption) => {
                                            if (selectedOption) handleChange('language', selectedOption.value);
                                        }}
                                        placeholder={t('Document Language')}
                                        isMultiple={false}
                                        direction={direction}
                                        portalMenu
                                    />
                                </div>
                                {shouldShowDesignVersionPicker(designVersions) && (
                                    <DesignVersionField
                                        versions={designVersions}
                                        value={formData.designVersionId}
                                        onChange={(id) => handleChange('designVersionId', id)}
                                        t={t}
                                        direction={direction}
                                        className={styles.languageField}
                                        titleClassName={styles.fieldTitle}
                                    />
                                )}
                                {client?.managesProject && invoiceModalType === 5 && (
                                    <div className={styles.projectsField} dir={direction}>
                                        <div className={styles.fieldTitle}>
                                            {t('Projects')}
                                        </div>
                                        <AutocompleteSelectFilter
                                            options={segmentationAutocompleteOptions}
                                            selectedValues={segmentationAutocompleteValue}
                                            onChange={handleSegmentationAutocompleteChange}
                                            placeholder={t('Projects')}
                                            isMultiple={false}
                                            direction={direction}
                                            searchable
                                            clearable
                                            portalMenu
                                            isDisabled={projectCreateLoading}
                                        />
                                    </div>
                                )}
                                </div>
                            {formData.forceNoTax && (
                                <Typography 
                                    variant="caption" 
                                    color="text.secondary" 
                                    sx={{ 
                                        mt: 1, 
                                        display: 'block',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    {formData.priceIncludingVAT && allItemsHaveNoVATCalculation() 
                                        ? t('ForceNoTaxAutoEnabledDueToAllItemsNoVAT') 
                                        : t('VATControlsDisabledDueToNoTax')
                                    }
                                </Typography>
                            )}
                            </>
                            )}
                            {/* Invoice Items Table */}
                            {invoiceModalType !== 6 && (
                                <Box sx={{ mb: 1, dir: direction }}>
                                    {/* Table Mode for all modes */}
                                    {(() => {
                                    const showMakatQty = itemsDisplayMode !== 'descriptionOnly';
                                    const showVATCol = !formData.forceNoTax;
                                    return (
                                    <>


                                            <StyledTableContainer dir={direction} className={styles.tableWrapper}>
                                                <StyledTable dir={direction} className={styles.invoiceItemsTable}>
                                                    <colgroup>
                                                        <col className={styles.colItemList} />
                                                        {showMakatQty && <col className={styles.colMakat} />}
                                                        <col className={styles.colDescription} />
                                                        {showMakatQty && <col className={styles.colQuantity} />}
                                                        <col className={styles.colPrice} />
                                                        {showVATCol && <col className={styles.colVat} />}
                                                        <col className={styles.colDiscountPct} />
                                                        <col className={styles.colDiscountAmount} />
                                                        <col className={styles.colTotal} />
                                                        <col className={styles.colActions} />
                                                    </colgroup>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell scope="col" className={styles.colItemList} sx={{ dir: direction }}>{t('ItemList')}</TableCell>
                                                            {showMakatQty && (
                                                                <TableCell scope="col" className={styles.colMakat} sx={{ dir: direction }}>{t('Makat')}</TableCell>
                                                            )}
                                                            <TableCell scope="col" className={styles.colDescription} sx={{ dir: direction }}>{t('ItemDescription')}</TableCell>
                                                            {showMakatQty && (
                                                                <TableCell scope="col" className={styles.colQuantity} sx={{ dir: direction }}>{t('Quantity')}</TableCell>
                                                            )}
                                                            {formData.foreignCurrencyCode != null ? (
                                                                <TableCell scope="col" className={styles.colPrice} sx={{ dir: direction }}>{t('CurrencyAmount')}</TableCell>
                                                            ) : (
                                                                <TableCell scope="col" className={styles.colPrice} sx={{ dir: direction }}>{t('Price')}</TableCell>
                                                            )}
                                                            {showVATCol && (
                                                                <TableCell scope="col" className={styles.colVat} sx={{ dir: direction }}>{t('ItemVATOption')}</TableCell>
                                                            )}
                                                            <TableCell scope="col" className={styles.colDiscountPct} sx={{ dir: direction }}>{t('Discount')} %</TableCell>
                                                            <TableCell scope="col" className={styles.colDiscountAmount} sx={{ dir: direction }}>{t('DiscountAmount')}</TableCell>
                                                            <TableCell scope="col" className={styles.colTotal} sx={{ dir: direction }}>{t('Total')}</TableCell>
                                                            <TableCell scope="col" className={styles.colActions} sx={{ dir: direction }}>{t('Actions')}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {formData.invoiceItems.map((item, index) => {
                                                            const { discountAmount, totalAfterDiscount } = computeRowTotals(item);

                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell className={styles.colItemList} sx={{ dir: direction }}>
                                                                        <StyledButton
                                                                            variant="outlined"
                                                                            size="small"
                                                                                    onClick={() => handleOpenItemSelectionModal(index)}
                                                                                    startIcon={<EditIcon />}
                                                                                    dir={direction}
                                                                                    fullWidth
                                                                                    aria-label={t('ItemList')}
                                                                                >
                                                                                    {t('ItemList')}
                                                                                </StyledButton>
                                                                            </TableCell>

                                                                    {showMakatQty ? (
                                                                            <TableCell className={styles.colMakatDesc} colSpan={2} sx={{ dir: direction }}>
                                                                                <div className={styles.makatDescGroup}>
                                                                                    <div className={styles.makatSlot}>
                                                                                        <TextField
                                                                                            fullWidth
                                                                                            placeholder={t('Makat')}
                                                                                            value={item.makat}
                                                                                            type="text"
                                                                                            onChange={(e) => handleItemChange(index, 'makat', e.target.value)}
                                                                                            onBlur={(e) => handleMakatBlurOrEnter?.(index, e.target.value)}
                                                                                            onKeyDown={(e) => {
                                                                                                if (e.key === 'Enter') {
                                                                                                    handleMakatBlurOrEnter?.(index, e.target.value);
                                                                                                }
                                                                                            }}
                                                                                            error={!!fieldErrors.invoiceItems?.[index]?.makat}
                                                                                            dir={direction}
                                                                                            size="small"
                                                                                            required
                                                                                            className={getFieldClassName(!!fieldErrors.invoiceItems?.[index]?.makat)}
                                                                                            variant="standard"
                                                                                            InputProps={comboInputProps}
                                                                                            inputProps={{
                                                                                                'aria-label': t('Makat'),
                                                                                                'aria-invalid': !!fieldErrors.invoiceItems?.[index]?.makat,
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                    <div className={styles.descSlot}>
                                                                                        <Autocomplete
                                                                                            freeSolo
                                                                                            size="small"
                                                                                            className={`${fieldStyles.combo} ${styles.itemAutocomplete}`}
                                                                                            options={clientItems || []}
                                                                                            getOptionLabel={(opt) =>
                                                                                                typeof opt === 'string'
                                                                                                    ? opt
                                                                                                    : `${opt?.name || ''}${itemsDisplayMode !== 'descriptionOnly' && opt?.makat ? ` (${opt.makat})` : ''}`
                                                                                            }
                                                                                            inputValue={item.name ?? ''}
                                                                                            onInputChange={(e, v, reason) => {
                                                                                                // Only sync user-driven input changes to the name.
                                                                                                // Ignore MUI's internal "reset" which would overwrite
                                                                                                // the name with the full option label like "name (makat)".
                                                                                                if (reason === 'reset') return;
                                                                                                handleItemChange(index, 'name', v ?? '');
                                                                                            }}
                                                                                            onChange={(e, val) => {
                                                                                                if (val && typeof val !== 'string') {
                                                                                                    handleInvoiceItem(index, { value: val.makat, label: val.name });
                                                                                                }
                                                                                            }}
                                                                                            dir={direction}
                                                                                            PopperComponent={renderComboPopper}
                                                                                            renderInput={(params) => (
                                                                                                <TextField
                                                                                                    {...params}
                                                                                                    fullWidth
                                                                                                    multiline
                                                                                                    size="small"
                                                                                                    variant="standard"
                                                                                                    placeholder={t('ItemDescription')}
                                                                                                    error={!!fieldErrors.invoiceItems?.[index]?.name}
                                                                                                    required
                                                                                                    className={getTableMultilineClassName(
                                                                                                        !!fieldErrors.invoiceItems?.[index]?.name,
                                                                                                        styles.tableInputMultiline
                                                                                                    )}
                                                                                                    InputProps={tableMultilineInputProps(params.InputProps)}
                                                                                                    inputProps={{
                                                                                                        ...params.inputProps,
                                                                                                        rows: 2,
                                                                                                        'aria-label': t('ItemDescription'),
                                                                                                        'aria-invalid': !!fieldErrors.invoiceItems?.[index]?.name,
                                                                                                    }}
                                                                                                />
                                                                                            )}
                                                                                            componentsProps={{
                                                                                                popper: { sx: { zIndex: 9999 } },
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                    {/* Per-item note shown on the PDF only (not persisted in DB). Spans Makat + Description. */}
                                                                                    <div className={styles.notesSlot}>
                                                                                        <TextField
                                                                                            fullWidth
                                                                                            multiline
                                                                                            variant="standard"
                                                                                            placeholder={t('ItemNotesPlaceholder')}
                                                                                            value={item.notes ?? ''}
                                                                                            onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                                                                                            dir={direction}
                                                                                            size="small"
                                                                                            InputProps={tableMultilineInputProps(comboInputProps)}
                                                                                            className={getTableMultilineClassName(false, styles.tableInputNotes)}
                                                                                            inputProps={{
                                                                                                rows: 2,
                                                                                                maxLength: 1000,
                                                                                                'aria-label': t('ItemNotesPlaceholder'),
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </TableCell>
                                                                    ) : (
                                                                    <TableCell className={styles.colDescription} sx={{ dir: direction }}>
                                                                        <Autocomplete
                                                                            freeSolo
                                                                            size="small"
                                                                            className={`${fieldStyles.combo} ${styles.itemAutocomplete}`}
                                                                            options={clientItems || []}
                                                                            getOptionLabel={(opt) =>
                                                                                typeof opt === 'string'
                                                                                    ? opt
                                                                                    : `${opt?.name || ''}${itemsDisplayMode !== 'descriptionOnly' && opt?.makat ? ` (${opt.makat})` : ''}`
                                                                            }
                                                                            inputValue={item.name ?? ''}
                                                                            onInputChange={(e, v, reason) => {
                                                                                // Only sync user-driven input changes to the name.
                                                                                // Ignore MUI's internal "reset" which would overwrite
                                                                                // the name with the full option label like "name (makat)".
                                                                                if (reason === 'reset') return;
                                                                                handleItemChange(index, 'name', v ?? '');
                                                                            }}
                                                                            onChange={(e, val) => {
                                                                                if (val && typeof val !== 'string') {
                                                                                    handleInvoiceItem(index, { value: val.makat, label: val.name });
                                                                                }
                                                                            }}
                                                                            dir={direction}
                                                                            PopperComponent={renderComboPopper}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    fullWidth
                                                                                    multiline
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    placeholder={t('ItemDescription')}
                                                                                    error={!!fieldErrors.invoiceItems?.[index]?.name}
                                                                                    required
                                                                                    className={getTableMultilineClassName(
                                                                                        !!fieldErrors.invoiceItems?.[index]?.name,
                                                                                        styles.tableInputMultiline
                                                                                    )}
                                                                                    InputProps={tableMultilineInputProps(params.InputProps)}
                                                                                    inputProps={{
                                                                                        ...params.inputProps,
                                                                                        rows: 2,
                                                                                        'aria-label': t('ItemDescription'),
                                                                                        'aria-invalid': !!fieldErrors.invoiceItems?.[index]?.name,
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            componentsProps={{
                                                                                popper: { sx: { zIndex: 9999 } },
                                                                            }}
                                                                        />
                                                                        {/* Per-item note shown on the PDF only (not persisted in DB). */}
                                                                        <TextField
                                                                            fullWidth
                                                                            multiline
                                                                            variant="standard"
                                                                            placeholder={t('ItemNotesPlaceholder')}
                                                                            value={item.notes ?? ''}
                                                                            onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                                                                            dir={direction}
                                                                            size="small"
                                                                            InputProps={tableMultilineInputProps(comboInputProps)}
                                                                            className={getTableMultilineClassName(false, styles.tableInputNotes)}
                                                                            inputProps={{
                                                                                rows: 2,
                                                                                maxLength: 1000,
                                                                                'aria-label': t('ItemNotesPlaceholder'),
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    )}
                                                                    {showMakatQty && (
                                                                        <TableCell className={styles.colQuantity} sx={{ dir: direction }}>
                                                                            <TextField
                                                                                fullWidth
                                                                                placeholder={t('Quantity')}
                                                                                value={item.quantity}
                                                                                type="number"
                                                                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                                                error={!!fieldErrors.invoiceItems?.[index]?.quantity}
                                                                                dir={direction}
                                                                                size="small"
                                                                                required
                                                                                className={getFieldClassName(!!fieldErrors.invoiceItems?.[index]?.quantity)}
                                                                                    variant="standard"
                                                                                    InputProps={comboInputProps}
                                                                                inputProps={{
                                                                                    'aria-label': t('Quantity'),
                                                                                    'aria-invalid': !!fieldErrors.invoiceItems?.[index]?.quantity,
                                                                                    inputMode: 'decimal',
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                    )}
                                                                    {formData.foreignCurrencyCode != null ? (
                                                                        <TableCell className={styles.colPrice} sx={{ dir: direction }}>
                                                                            <TextField
                                                                                fullWidth
                                                                                placeholder={t('CurrencyAmount')}
                                                                                value={item.foreignCurrencyPrice}
                                                                                type="number"
                                                                                onChange={(e) => handleChangeItemPrice(index, e.target.value)}
                                                                                error={!!fieldErrors.invoiceItems?.[index]?.price}
                                                                                dir={direction}
                                                                                size="small"
                                                                                required
                                                                                className={getFieldClassName(!!fieldErrors.invoiceItems?.[index]?.price)}
                                                                                    variant="standard"
                                                                                    InputProps={comboInputProps}
                                                                                inputProps={{
                                                                                    'aria-label': t('CurrencyAmount'),
                                                                                    'aria-invalid': !!fieldErrors.invoiceItems?.[index]?.price,
                                                                                    inputMode: 'decimal',
                                                                                    step: 'any',
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                    ) : (
                                                                        <TableCell className={styles.colPrice} sx={{ dir: direction }}>
                                                                            <TextField
                                                                                fullWidth
                                                                                placeholder={t('Price')}
                                                                                value={item.price}
                                                                                type="number"
                                                                                onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                                                                error={!!fieldErrors.invoiceItems?.[index]?.price}
                                                                                dir={direction}
                                                                                size="small"
                                                                                required
                                                                                className={getFieldClassName(!!fieldErrors.invoiceItems?.[index]?.price)}
                                                                                    variant="standard"
                                                                                    InputProps={comboInputProps}
                                                                                inputProps={{
                                                                                    'aria-label': t('Price'),
                                                                                    'aria-invalid': !!fieldErrors.invoiceItems?.[index]?.price,
                                                                                    inputMode: 'decimal',
                                                                                    step: 'any',
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                    )}
                                                                    {showVATCol && (
                                                                        <TableCell className={styles.colVat} sx={{ dir: direction }}>
                                                                            <Tooltip
                                                                                title={t('IndividualItemVATControl')}
                                                                                placement="top"
                                                                                arrow
                                                                            >
                                                                                <Box className={styles.filterSoftBg}>
                                                                                    <AutocompleteSelectFilter
                                                                                        options={[
                                                                                            { value: true, label: t('VATCalculation') },
                                                                                            { value: false, label: t('NoVATCalculation') }
                                                                                        ]}
                                                                                        selectedValues={
                                                                                            (item.includeVat !== undefined && item.includeVat !== null ? item.includeVat : true)
                                                                                            ? { value: true, label: t('VATCalculation') }
                                                                                                : { value: false, label: t('NoVATCalculation') }
                                                                                        }
                                                                                        onChange={(e, newValue) => {
                                                                                            handleItemVATChange(index, newValue ? newValue.value : true);
                                                                                        }}
                                                                                        placeholder={t('ItemVATOption')}
                                                                                        isMultiple={false}
                                                                                        direction={direction}
                                                                                        portalMenu
                                                                                    />
                                                                                </Box>
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                    )}
                                                                    <TableCell className={styles.colDiscountPct} sx={{ dir: direction }}>
                                                                        <TextField
                                                                            fullWidth
                                                                            placeholder={t('Discount')}
                                                                            value={item.discount}
                                                                            type="number"
                                                                            onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                                                                            error={!!fieldErrors.invoiceItems?.[index]?.discount}
                                                                            dir={direction}
                                                                            size="small"
                                                                            inputProps={{
                                                                                min: 0,
                                                                                max: 100,
                                                                                'aria-label': `${t('Discount')} %`,
                                                                                'aria-invalid': !!fieldErrors.invoiceItems?.[index]?.discount,
                                                                                inputMode: 'decimal',
                                                                            }}
                                                                            required
                                                                            className={getFieldClassName(!!fieldErrors.invoiceItems?.[index]?.discount)}
                                                                            variant="standard"
                                                                            InputProps={comboInputProps}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell className={styles.colDiscountAmount} sx={{ dir: direction }}>
                                                                        <TextField
                                                                            fullWidth
                                                                            placeholder={t('DiscountAmount')}
                                                                            value={
                                                                                item.discountAmount !== undefined && item.discountAmount !== null
                                                                                    ? item.discountAmount
                                                                                    : (Math.round(discountAmount * 100) / 100)
                                                                            }
                                                                            type="number"
                                                                            onChange={(e) =>
                                                                                handleItemDiscountAmountChange
                                                                                    ? handleItemDiscountAmountChange(index, e.target.value)
                                                                                    : null
                                                                            }
                                                                            dir={direction}
                                                                            size="small"
                                                                            inputProps={{
                                                                                min: 0,
                                                                                'aria-label': t('DiscountAmount'),
                                                                                inputMode: 'decimal',
                                                                            }}
                                                                            className={getFieldClassName(false)}
                                                                            variant="standard"
                                                                            InputProps={comboInputProps}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell className={styles.colTotal} sx={{ dir: direction }}>
                                                                        <Tooltip title={String(totalAfterDiscount)} arrow placement="top">
                                                                            <Typography
                                                                                variant="body2"
                                                                                fontWeight={600}
                                                                                color="primary.main"
                                                                                aria-label={`${t('Total')}: ${beautifyNumber(totalAfterDiscount)}`}
                                                                                sx={{
                                                                                    dir: direction,
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap',
                                                                                    fontVariantNumeric: 'tabular-nums',
                                                                                    padding: '6px 4px',
                                                                                }}
                                                                            >
                                                                                {beautifyNumber(totalAfterDiscount)}
                                                                            </Typography>
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                    <TableCell className={styles.colActions} sx={{ dir: direction }}>
                                                                        <Tooltip title={t('RemoveItem')} arrow placement="top">
                                                                            <IconButton
                                                                                color="error"
                                                                                onClick={() => handleRemoveItem(index)}
                                                                                size="small"
                                                                                aria-label={t('RemoveItem')}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </StyledTable>
                                            </StyledTableContainer>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', dir: direction , mt: 1}}>
                                                <StyledButton
                                                    variant="contained"
                                                    onClick={handleAddItem}
                                                    startIcon={<AddIcon />}
                                                    dir={direction}
                                                    size="small"
                                                >
                                                    {t('AddItem')}
                                                </StyledButton>
                                            </Box>
                                        </>
                                    );
                                    })()}
                                </Box>
                            )}
                        </StyledFormSection>
                    </StyledFormCard>
                )}

                {/* Receipt Items Section */}
                {(invoiceModalType === 5 || invoiceModalType === 6) && (
                    <StyledFormCard  dir={direction}>
                        <StyledFormSection dir={direction}>
                            <StyledSectionHeader dir={direction}>
                                <StyledSectionTitle variant="h5" dir={direction}>
                                    <PaymentIcon color="primary" />
                                    {t('ReceiptItems')}
                                </StyledSectionTitle>
                                <Chip
                                    label={selectedPaymentMethod 
                                        ? getPaymentMethodDisplayName(selectedPaymentMethod)
                                        : `${formData.receiptItems?.length || 0} ${t('Items')}`
                                    }
                                    color="secondary"
                                    size="small"
                                />
                            </StyledSectionHeader>
                            {invoiceModalType === 6 && (
                            <div className={styles.languageProjectsRow} dir={direction}>
                                {/* Document Language Selection for normal receipts (invoiceModalType === 6) */}
                                    <div className={styles.languageField}>
                                        <div className={styles.fieldTitle}>
                                            {t('Document Language')}
                                        </div>
                                        <AutocompleteSelectFilter
                                            options={[
                                                { value: 'he', label: 'עברית' },
                                                { value: 'ar', label: 'العربية' },
                                                { value: 'en', label: 'English' }
                                            ]}
                                            selectedValues={(() => {
                                                const lang = formData.language || currentUILanguage || 'he';
                                                const options = [
                                                    { value: 'he', label: 'עברית' },
                                                    { value: 'ar', label: 'العربية' },
                                                    { value: 'en', label: 'English' }
                                                ];
                                                return options.find((o) => o.value === lang) || options[0];
                                            })()}
                                            onChange={(_, selectedOption) => {
                                                if (selectedOption) handleChange('language', selectedOption.value);
                                            }}
                                            placeholder={t('Document Language')}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                        />
                                    </div>
                                    {shouldShowDesignVersionPicker(designVersions) && (
                                        <DesignVersionField
                                            versions={designVersions}
                                            value={formData.designVersionId}
                                            onChange={(id) => handleChange('designVersionId', id)}
                                            t={t}
                                            direction={direction}
                                            className={styles.languageField}
                                            titleClassName={styles.fieldTitle}
                                        />
                                    )}
                                {client?.managesProject && (
                                <div className={styles.projectsField} dir={direction}>
                                    <div className={styles.fieldTitle}>
                                        {t('Projects')}
                                    </div>
                                    <AutocompleteSelectFilter
                                        options={segmentationAutocompleteOptions}
                                        selectedValues={segmentationAutocompleteValue}
                                        onChange={handleSegmentationAutocompleteChange}
                                        placeholder={t('Projects')}
                                        isMultiple={false}
                                        direction={direction}
                                        searchable
                                        clearable
                                        portalMenu
                                        isDisabled={projectCreateLoading}
                                    />
                                </div>
                                )}
                            </div>
                            )}

                            {/* Payment Method Buttons */}
                            <Stack direction="row" spacing={1} sx={{ mb: 3, dir: direction }} flexWrap="wrap">
                                <StyledButton
                                    variant="outlined"
                                    onClick={() => handlePaymentMethodSelect('checks')}
                                    startIcon={<ReceiptIcon />}
                                    dir={direction}
                                    size="small"
                                >
                                    {t('AddCheck')}
                                </StyledButton>
                                <StyledButton
                                    variant="outlined"
                                    onClick={() => handlePaymentMethodSelect('creditCards')}
                                    startIcon={<CreditCardIcon />}
                                    dir={direction}
                                    size="small"
                                >
                                    {t('AddCreditCard')}
                                </StyledButton>
                                <StyledButton
                                    variant="outlined"
                                    onClick={() => handlePaymentMethodSelect('cash')}
                                    startIcon={<LocalAtmIcon />}
                                    dir={direction}
                                    size="small"
                                >
                                    {t('AddCash')}
                                </StyledButton>
                                <StyledButton
                                    variant="outlined"
                                    onClick={() => handlePaymentMethodSelect('bankTransactions')}
                                    startIcon={<AccountBalanceIcon />}
                                    dir={direction}
                                    size="small"
                                >
                                    {t('AddBankTransfer')}
                                </StyledButton>
                                <StyledButton
                                    variant="outlined"
                                    onClick={() => handlePaymentMethodSelect('offsets')}
                                    startIcon={<LocalAtmIcon />}
                                    dir={direction}
                                    size="small"
                                >
                                    {t('AddOffset')}
                                </StyledButton>
                                <StyledButton
                                    variant="outlined"
                                    onClick={handleAddWithholdingTaxClick}
                                    disabled={withholdingTaxRowAdded}
                                    startIcon={<CalculateIcon />}
                                    dir={direction}
                                    size="small"
                                >
                                    {t('AddWithholdingTax')}
                                </StyledButton>
                                {invoiceModalType === 5 && (() => {
                                    // Only allow rounding when the gap between invoice and receipt
                                    // totals is small (≤ 1 shekel). Anything larger almost certainly
                                    // means a real entry mistake, not a rounding artifact.
                                    const diffAbs = Math.abs(
                                        (Number(totalInvoiceAmount) || 0) - (Number(totalReceiptAmount) || 0)
                                    );
                                    const canRound = diffAbs > 0.005 && diffAbs <= 1;
                                    return (
                                        <Tooltip
                                            title={canRound ? t('RoundReceiptTooltip') : t('RoundReceiptDisabledTooltip')}
                                            arrow
                                            placement="top"
                                        >
                                            <span>
                                                <StyledButton
                                                    variant="outlined"
                                                    onClick={handleRoundReceipt}
                                                    disabled={!canRound}
                                                    startIcon={<RoundedCornerIcon />}
                                                    dir={direction}
                                                    size="small"
                                                >
                                                    {t('RoundReceipt')}
                                                </StyledButton>
                                            </span>
                                        </Tooltip>
                                    );
                                })()}
                            </Stack>

                            {renderReceiptBankDetailsBar?.()}

                            {/* Receipt Items Table */}
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateAdapterLocale}>
                            <StyledTableContainer dir={direction} className={styles.tableWrapper}>
                                <StyledTable dir={direction} className={`${styles.invoiceItemsTable} ${styles.receiptItemsTable}`}>
                                    <colgroup>
                                        <col className={styles.colReceiptType} />
                                        <col className={styles.colReceiptDesc} />
                                        <col className={styles.colReceiptBankName} />
                                        <col className={styles.colReceiptBankCode} />
                                        <col className={styles.colReceiptBankBranch} />
                                        <col className={styles.colReceiptBankAccount} />
                                        <col className={styles.colReceiptAmount} />
                                        <col className={styles.colReceiptReference} />
                                        <col className={styles.colReceiptDate} />
                                        <col className={styles.colReceiptUpload} />
                                        <col className={styles.colReceiptRemove} />
                                    </colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell scope="col" className={styles.colReceiptType} sx={{ dir: direction }}>{t('Type')}</TableCell>
                                            <TableCell scope="col" className={styles.colReceiptDesc} sx={{ dir: direction }}>{t('CheckNumber')} / {t('Description')}</TableCell>
                                            <TableCell scope="col" className={styles.colReceiptBankName} sx={{ dir: direction }}>{t('BankName')}</TableCell>
                                            <TableCell scope="col" className={styles.colReceiptBankCode} sx={{ dir: direction }}>{t('BankCode')}</TableCell>
                                            <TableCell scope="col" className={styles.colReceiptBankBranch} sx={{ dir: direction }}>{t('BankBranchCode')}</TableCell>
                                            <TableCell scope="col" className={styles.colReceiptBankAccount} sx={{ dir: direction }}>{t('BankAccountNumber')}</TableCell>
                                            <TableCell scope="col" className={styles.colReceiptAmount} sx={{ dir: direction }}>{formData.foreignCurrencyCode != null ? t('CurrencyAmount') : t('Amount')}</TableCell>
                                            <TableCell scope="col" className={styles.colReceiptReference} sx={{ dir: direction }}>{t('Reference2')}</TableCell>
                                            <TableCell scope="col" className={styles.colReceiptDate} sx={{ dir: direction }}>{t('DuePaymentDate')}</TableCell>
                                            <TableCell scope="col" className={styles.colReceiptUpload} sx={{ dir: direction }}>{t('Actions')}</TableCell>
                                            <TableCell scope="col" className={styles.colReceiptRemove} sx={{ dir: direction }} aria-hidden="true"></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {renderReceiptRows()}

                                        {withholdingTaxRowAdded && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={6}
                                                    align="left"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        dir: direction,
                                                        textAlign: 'left !important',
                                                    }}
                                                >
                                                    {t('WithholdingTax')}
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'middle' }}>
                                                    <TextField
                                                        fullWidth
                                                        placeholder={t('WithholdingTax')}
                                                        type="number"
                                                        value={formData.invoiceWithholdingTax || ''}
                                                        onChange={(e) => handleChange('invoiceWithholdingTax', e.target.value)}
                                                        dir={direction}
                                                        size="small"
                                                        variant="standard"
                                                        className={getFieldClassName(false)}
                                                        InputProps={comboInputProps}
                                                        disabled={
                                                            autoClientWithholding ||
                                                            (!!withholdingPercent && !isNaN(Number(withholdingPercent)))
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    className={styles.colReceiptReference}
                                                    sx={{ verticalAlign: 'middle' }}
                                                >
                                                    <TextField
                                                        fullWidth
                                                        placeholder={t('WithholdingPercentage', { defaultValue: '%' })}
                                                        type="number"
                                                        value={withholdingPercent}
                                                        onChange={(e) =>
                                                            handleWithholdingPercentChange?.(e.target.value)
                                                        }
                                                        dir={direction}
                                                        size="small"
                                                        variant="standard"
                                                        disabled={autoClientWithholding}
                                                        className={getFieldClassName(false)}
                                                        InputProps={{
                                                            ...comboInputProps,
                                                            endAdornment: (
                                                                <InputAdornment position="end">%</InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    colSpan={2}
                                                    sx={{ verticalAlign: 'middle' }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'stretch',
                                                            width: '100%',
                                                            gap: 0.75,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                justifyContent: direction === 'rtl' ? 'flex-start' : 'flex-end',
                                                            }}
                                                        >
                                                            <IconButton
                                                                color="error"
                                                                onClick={() => {
                                                                    if (handleRemoveWithholdingTaxRow) {
                                                                        handleRemoveWithholdingTaxRow();
                                                                    } else {
                                                                        setWithholdingTaxRowAdded(false);
                                                                        handleChange('invoiceWithholdingTax', '');
                                                                    }
                                                                }}
                                                                size="small"
                                                                aria-label={t('Delete')}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Box>
                                                        {receiptClientDeductionRatePercent != null && (
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={!!autoClientWithholding}
                                                                        onChange={(e) =>
                                                                            handleReceiptAutoWithholdingChange?.(
                                                                                e.target.checked
                                                                            )
                                                                        }
                                                                        size="small"
                                                                    />
                                                                }
                                                                label={t('UseSupplierDeductionRate', {
                                                                    rate: receiptClientDeductionRatePercent,
                                                                    defaultValue: `Use supplier deduction rate (${receiptClientDeductionRatePercent}%)`,
                                                                })}
                                                                sx={{
                                                                    direction: direction === 'rtl' ? 'ltr' : 'rtl',
                                                                    alignItems: 'flex-start',
                                                                    width: '100%',
                                                                    '& .MuiFormControlLabel-label': {
                                                                        fontSize: '12px',
                                                                        whiteSpace: 'normal',
                                                                        lineHeight: 1.35,
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                        {(autoClientWithholding || (!!withholdingPercent && !isNaN(Number(withholdingPercent)))) && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    direction,
                                                                    textAlign:
                                                                        direction === 'rtl' ? 'left' : 'right',
                                                                    color: '#666',
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                {t('CalculatedWithholdingAmount', {
                                                                    amount: (
                                                                        Number.isFinite(calculatedWithholdingAmount)
                                                                            ? calculatedWithholdingAmount
                                                                            : Number.isFinite(receiptWithholdingFormulaAmount)
                                                                              ? receiptWithholdingFormulaAmount
                                                                              : 0
                                                                    ).toFixed(2),
                                                                    defaultValue: `Calculated withholding: ₪${(
                                                                        Number.isFinite(calculatedWithholdingAmount)
                                                                            ? calculatedWithholdingAmount
                                                                            : Number.isFinite(receiptWithholdingFormulaAmount)
                                                                              ? receiptWithholdingFormulaAmount
                                                                              : 0
                                                                    ).toFixed(2)}`,
                                                                })}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>

                                            </TableRow>
                                        )}
                                    </TableBody>
                                </StyledTable>
                            </StyledTableContainer>
                            </LocalizationProvider>
                        </StyledFormSection>
                    </StyledFormCard>
                )}

                {/* Action Buttons */}
                <StyledFormCard sx={{ mb: 3 }} dir={direction}>
                    <StyledFormSection dir={direction}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, dir: direction }}>
                            <StyledButton
                                variant="contained"
                                size="medium"
                                onClick={allClientItemFieldsFilled ? handleCreateDraftInvoice : null}
                                disabled={!allClientItemFieldsFilled}
                                startIcon={<SaveIcon />}
                                sx={{ minWidth: 150 }}
                                dir={direction}
                            >
                                {t('CreateDraft')} {t(type)}
                            </StyledButton>
                        </Box>
                    </StyledFormSection>
                </StyledFormCard>
                    
                {/* Floating Totals */}

            </StyledFormContainer>

            {/* Receipt Customization Dialog */}
            <ReceiptCustomizationDialog
                open={customizationDialogOpen}
                onClose={handleCloseCustomizationDialog}
                onSave={handleCustomizationSave}
                existingSignature={client?.signature || ''}
                existingProfilePicture={client?.profilePicture || ''}
                existingDescription={formData.invoiceDescription || ''}
                existingFooter={formData.invoiceFooter || ''}
                token={token}
                accountantId={client?.accountantId}
                caseId={client?.caseId}
                direction={direction}
                formData={formData}
                client={client}
                invoiceModalType={invoiceModalType}
            />

            {/* Item Selection Modal */}
            <ItemSelectionModal
                open={itemSelectionModal.open}
                onClose={handleCloseItemSelectionModal}
                onConfirm={handleConfirmItemSelection}
                items={clientItems}
                selectedItem={selectedItem}
                direction={direction}
                initialItemName={selectedItem?.name || ''}
            />

            <ProjectModal
                open={projectModalOpen}
                mode="create"
                direction={direction}
                formData={{ code: '', name: '' }}
                existingProjects={segmentationProjects}
                onClose={handleProjectModalClose}
                onSave={handleProjectModalSave}
                loading={projectCreateLoading}
            />

        </ThemeProvider>
    );
};

export default EnhancedCreateReceipt;
